import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import {
  TOV_SCOPE_TYPES,
  TOV_VISUAL_RULE_TYPES,
} from "contracts/enums/tov-voice";
import { For, Show } from "solid-js";
import { TOV_FIELD_KIND } from "../../enums/tov";
import { type CardEditProps, EditableCard, type FieldSpec } from "./EditForms";
import { CardGrid } from "./primitives";
import { Group } from "./VoiceCards";

const AUDIENCE_FIELDS: FieldSpec[] = [
  { name: "key", label: "Key", kind: TOV_FIELD_KIND.TEXT },
  { name: "label", label: "Label", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "description",
    label: "Description",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
  { name: "needs", label: "Needs", kind: TOV_FIELD_KIND.TEXT, grow: true },
  {
    name: "painPoints",
    label: "Pain points",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];
const PILLAR_FIELDS: FieldSpec[] = [
  { name: "key", label: "Key", kind: TOV_FIELD_KIND.TEXT },
  { name: "label", label: "Label", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "description",
    label: "Description",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
  {
    name: "objective",
    label: "Objective",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
  {
    name: "weight",
    label: "Weight",
    kind: TOV_FIELD_KIND.NUMBER,
    optional: true,
  },
];
const JOURNEY_FIELDS: FieldSpec[] = [
  { name: "key", label: "Key", kind: TOV_FIELD_KIND.TEXT },
  { name: "label", label: "Label", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "description",
    label: "Description",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];
const VISUAL_FIELDS: FieldSpec[] = [
  {
    name: "scopeType",
    label: "Scope",
    kind: TOV_FIELD_KIND.SELECT,
    options: TOV_SCOPE_TYPES,
  },
  {
    name: "scopeKey",
    label: "Scope key",
    kind: TOV_FIELD_KIND.TEXT,
    optional: true,
  },
  {
    name: "visualRuleType",
    label: "Type",
    kind: TOV_FIELD_KIND.SELECT,
    options: TOV_VISUAL_RULE_TYPES,
  },
  { name: "title", label: "Title", kind: TOV_FIELD_KIND.TEXT },
  {
    name: "description",
    label: "Description",
    kind: TOV_FIELD_KIND.TEXT,
    grow: true,
  },
];

// Strategy tab: audiences, content pillars, journey stages, visual guidelines.
export function StrategyCards(props: {
  version: TovVersionResponse;
  edit: CardEditProps;
}) {
  const version = () => props.version;
  const edit = () => props.edit;
  return (
    <div class="tov-sections">
      <Group title="Audiences" empty={version().audiences.length === 0}>
        <CardGrid>
          <For each={version().audiences}>
            {(audience) => (
              <EditableCard
                title={audience.label}
                editable={edit().editable}
                fields={AUDIENCE_FIELDS}
                initial={{
                  key: audience.key,
                  label: audience.label,
                  description: audience.description,
                  needs: audience.needs,
                  painPoints: audience.painPoints,
                  displayOrder: audience.displayOrder,
                }}
                onSave={(values) =>
                  edit().onEdit("audiences", audience.id, values)
                }
                onDelete={() => edit().onDelete("audiences", audience.id)}
              >
                <p class="tov-desc">{audience.description}</p>
                <p class="tov-sub">Needs: {audience.needs}</p>
                <p class="tov-sub">Pain points: {audience.painPoints}</p>
              </EditableCard>
            )}
          </For>
        </CardGrid>
      </Group>

      <Group
        title="Content pillars"
        empty={version().contentPillars.length === 0}
      >
        <CardGrid>
          <For each={version().contentPillars}>
            {(pillar) => (
              <EditableCard
                title={pillar.label}
                tag={pillar.weight === null ? undefined : `w ${pillar.weight}`}
                editable={edit().editable}
                fields={PILLAR_FIELDS}
                initial={{
                  key: pillar.key,
                  label: pillar.label,
                  description: pillar.description,
                  objective: pillar.objective,
                  weight: pillar.weight ?? undefined,
                  displayOrder: pillar.displayOrder,
                }}
                onSave={(values) =>
                  edit().onEdit("content-pillars", pillar.id, values)
                }
                onDelete={() => edit().onDelete("content-pillars", pillar.id)}
              >
                <p class="tov-desc">{pillar.objective}</p>
                <p class="tov-sub">{pillar.description}</p>
              </EditableCard>
            )}
          </For>
        </CardGrid>
      </Group>

      <Group
        title="Journey stages"
        empty={version().journeyStages.length === 0}
      >
        <CardGrid>
          <For each={version().journeyStages}>
            {(stage) => (
              <EditableCard
                title={stage.label}
                editable={edit().editable}
                fields={JOURNEY_FIELDS}
                initial={{
                  key: stage.key,
                  label: stage.label,
                  description: stage.description,
                  displayOrder: stage.displayOrder,
                }}
                onSave={(values) =>
                  edit().onEdit("journey-stages", stage.id, values)
                }
                onDelete={() => edit().onDelete("journey-stages", stage.id)}
              >
                <p class="tov-desc">{stage.description}</p>
              </EditableCard>
            )}
          </For>
        </CardGrid>
      </Group>

      <Group
        title="Visual guidelines"
        empty={version().visualGuidelines.length === 0}
      >
        <CardGrid>
          <For each={version().visualGuidelines}>
            {(guideline) => (
              <EditableCard
                title={guideline.title}
                tag={guideline.visualRuleType}
                editable={edit().editable}
                fields={VISUAL_FIELDS}
                initial={{
                  scopeType: guideline.scopeType,
                  scopeKey: guideline.scopeKey ?? undefined,
                  visualRuleType: guideline.visualRuleType,
                  title: guideline.title,
                  description: guideline.description,
                  displayOrder: guideline.displayOrder,
                }}
                onSave={(values) =>
                  edit().onEdit("visual-guidelines", guideline.id, values)
                }
                onDelete={() =>
                  edit().onDelete("visual-guidelines", guideline.id)
                }
              >
                <p class="tov-desc">{guideline.description}</p>
                <Show when={guideline.scopeKey}>
                  <p class="tov-sub">Scope: {guideline.scopeKey}</p>
                </Show>
              </EditableCard>
            )}
          </For>
        </CardGrid>
      </Group>
    </div>
  );
}
